/* FGR4 / 09 — independent, progressive-enhancement product gallery.
   Native media-gallery remains the variant-select replacement boundary. */
(() => {
  'use strict';
  if (customElements.get('fgr4-gallery')) return;
  class FGR4Gallery extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.slides = Array.from(this.querySelectorAll('[data-f9-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-f9-thumb]'));
      this.dialog = this.querySelector('[data-f9-dialog]');
      this.zoomImage = this.querySelector('[data-f9-zoom-image]');
      this.zoomViewport = this.querySelector('[data-f9-zoom-viewport]');
      this.index = 0;
      this._touchX = 0;
      this.addEventListener('click', (event) => {
        const button = event.target.closest('button');
        if (!button || !this.contains(button)) return;
        if (button.matches('[data-f9-next]')) return void this.select(this.index + 1);
        if (button.matches('[data-f9-prev]')) return void this.select(this.index - 1);
        if (button.hasAttribute('data-f9-thumb')) return void this.select(Number(button.dataset.f9Thumb));
        if (button.matches('[data-f9-close]')) return void this.dialog?.close();
        if (button.matches('[data-f9-open]')) return void this.openZoom();
        if (button.matches('[data-f9-zoom]')) return void this.toggleZoom();
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') { event.preventDefault(); this.select(this.index - 1); }
        if (event.key === 'ArrowRight') { event.preventDefault(); this.select(this.index + 1); }
      });
      this.querySelector('[data-f9-slides]')?.addEventListener('touchstart', event => {
        this._touchX = event.changedTouches[0]?.screenX ?? 0;
      }, { passive: true });
      this.querySelector('[data-f9-slides]')?.addEventListener('touchend', event => {
        const diff = (event.changedTouches[0]?.screenX ?? 0) - this._touchX;
        if (Math.abs(diff) > 55 && this.slides.length > 1) {
          if (event.cancelable) event.preventDefault();
          this.select(this.index + (diff < 0 ? 1 : -1));
        }
      }, { passive: false });
      this.dialog?.addEventListener('close', () => this.resetZoom());
      this.select(0);
    }
    select(next) {
      const count = this.slides?.length ?? 0;
      if (!count) return;
      this.index = ((next % count) + count) % count;
      this.slides.forEach((slide, i) => { slide.hidden = i !== this.index; });
      this.thumbs.forEach((thumb, i) => {
        thumb.setAttribute('aria-pressed', i === this.index ? 'true' : 'false');
        if (i === this.index) {
          const strip = thumb.parentElement;
          if (strip && strip.scrollWidth > strip.clientWidth) {
            const left = thumb.getBoundingClientRect().left - strip.getBoundingClientRect().left + strip.scrollLeft;
            if (left < strip.scrollLeft || left + thumb.offsetWidth > strip.scrollLeft + strip.clientWidth) {
              strip.scrollTo({left: Math.max(0, left - (strip.clientWidth - thumb.offsetWidth) / 2), behavior: 'auto'});
            }
          }
        }
      });
      const label = String(this.index + 1).padStart(2, '0') + ' / ' + String(count).padStart(2, '0');
      this.querySelector('[data-f9-counter]')?.replaceChildren(document.createTextNode(label));
      this.querySelector('[data-f9-dialog-counter]')?.replaceChildren(document.createTextNode(label));
      if (this.dialog?.open) this.setZoomImage();
    }
    setZoomImage() {
      const slide = this.slides[this.index];
      if (!this.zoomImage || !slide) return;
      const poster = slide.querySelector('img');
      this.resetZoom();
      this.zoomImage.src = slide.dataset.f9Full || poster?.currentSrc || poster?.src || '';
      this.zoomImage.alt = slide.dataset.f9Alt || poster?.alt || '';
    }
    openZoom() {
      if (!this.dialog || !this.slides[this.index]?.querySelector('.f9-gallery__image')) return;
      this.setZoomImage();
      if (!this.dialog.open && typeof this.dialog.showModal === 'function') this.dialog.showModal();
    }
    resetZoom() {
      this.zoomViewport?.classList.remove('f9-gallery__dialog-viewport--zoomed');
      const control = this.querySelector('[data-f9-zoom]');
      if (control) { control.textContent = '+'; control.setAttribute('aria-label', 'Magnify current product image'); }
      if (this.zoomViewport) this.zoomViewport.scrollTop = this.zoomViewport.scrollLeft = 0;
    }
    toggleZoom() {
      if (!this.zoomViewport) return;
      const active = this.zoomViewport.classList.toggle('f9-gallery__dialog-viewport--zoomed');
      const control = this.querySelector('[data-f9-zoom]');
      if (control) { control.textContent = active ? '−' : '+'; control.setAttribute('aria-label', active ? 'Fit product image to screen' : 'Magnify current product image'); }
      if (active) {
        this.zoomViewport.scrollLeft = (this.zoomViewport.scrollWidth - this.zoomViewport.clientWidth) / 2;
        this.zoomViewport.scrollTop = (this.zoomViewport.scrollHeight - this.zoomViewport.clientHeight) / 2;
      } else this.zoomViewport.scrollTop = this.zoomViewport.scrollLeft = 0;
    }
  }
  customElements.define('fgr4-gallery', FGR4Gallery);
})();

/* FGR4 / The Hangar. Native buttons/links; progressive tab enhancement. */
(() => {
  'use strict';
  if (customElements.get('fgr4-flightdeck')) return;
  class FGR4Flightdeck extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.tabs = Array.from(this.querySelectorAll('[data-f9-flight-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-f9-flight-panel]'));
      this.addEventListener('click', e => {
        const tab = e.target.closest('[data-f9-flight-tab]');
        if (tab && this.contains(tab)) this.select(tab.dataset.f9FlightTab, false);
      });
      this.addEventListener('keydown', e => {
        const tab = e.target.closest('[data-f9-flight-tab]');
        if (!tab || !['ArrowRight','ArrowLeft','Home','End'].includes(e.key)) return;
        e.preventDefault();
        const at = this.tabs.indexOf(tab);
        const next = e.key === 'Home' ? 0 : e.key === 'End' ? this.tabs.length - 1 : (at + (e.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
        this.select(this.tabs[next]?.dataset.f9FlightTab, true);
      });
    }
    select(id, focus) {
      if (!id) return;
      const selected = this.tabs.find(tab => tab.dataset.f9FlightTab === id);
      if (!selected) return;
      this.tabs.forEach(tab => {
        const active = tab === selected;
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.tabIndex = active ? 0 : -1;
      });
      this.panels.forEach(panel => { panel.hidden = panel.dataset.f9FlightPanel !== id; });
      const index = this.tabs.indexOf(selected);
      const counter = this.closest('.f9-flightdeck')?.querySelector('[data-f10-hangar-counter]');
      if (counter) counter.textContent = String(index + 1).padStart(2, '0') + ' / ' + String(this.tabs.length).padStart(2, '0');
      const strip = selected.parentElement;
      if (strip && strip.scrollWidth > strip.clientWidth) {
        const offset = selected.getBoundingClientRect().left - strip.getBoundingClientRect().left + strip.scrollLeft;
        strip.scrollTo({left: Math.max(0, offset - (strip.clientWidth - selected.clientWidth) / 2), behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
      }
      if (focus) selected.focus();
    }
  }
  customElements.define('fgr4-flightdeck', FGR4Flightdeck);
})();

/* Collaboration photographic archive arrows; scroll snap also works without JS. */
document.addEventListener('click', (event) => {
  const arrow = event.target.closest('[data-f9-archive-prev],[data-f9-archive-next]');
  if (!arrow) return;
  const track = arrow.closest('.f9-partner-story__archive')?.querySelector('[data-f9-archive-track]');
  const card = track?.querySelector('.f9-partner-story__archive-item');
  if (!track || !card) return;
  const gap = Number.parseFloat(getComputedStyle(track).gap) || 12;
  const delta = card.getBoundingClientRect().width + gap;
  track.scrollBy({left: delta * (arrow.hasAttribute('data-f9-archive-next') ? 1 : -1), behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
});

/* Hangar arrows are outside the tab widget, but use its current tab state. */
document.addEventListener('click', event => {
 const button = event.target.closest('[data-f10-hangar-prev], [data-f10-hangar-next]');
 if (!button) return;
 const deck = button.closest('.f9-flightdeck')?.querySelector('fgr4-flightdeck');
 if (!deck?.tabs?.length) return;
 const current = Math.max(0, deck.tabs.findIndex(t => t.getAttribute('aria-selected') === 'true'));
 const next = (current + (button.hasAttribute('data-f10-hangar-next') ? 1 : -1) + deck.tabs.length) % deck.tabs.length;
 deck.select(deck.tabs[next]?.dataset.f9FlightTab, false);
});
